/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core'
import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpErrorResponse,
} from '@angular/common/http'
import { AuthService } from '../auth/auth.service'
import { Observable, throwError } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { Router } from '@angular/router'
import { AuthenticationLoginResponseModel } from '../../models/authenticationLoginResponse.model'

/**
 * Intercepts HTTP requests to add authentication tokens and handle unauthorized requests.

 * This interceptor is designed to:
 * - Add an authorization token to requests if available and valid.
 * - Redirect to the login page if the token is expired or the request is unauthorized.

 * @Injectable()
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    constructor(
        private _authService: AuthService,
        private _router: Router
    ) {}

    /**
     * Intercepts
     an HTTP request and adds an authorization header if necessary.

     * @param req {HttpRequest<any>} The HTTP request to be intercepted.
     * @param next {HttpHandler} The next handler in the request pipeline.
     * @returns {Observable<HttpEvent<any>>} An Observable of the HTTP event.
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Allow /dashboard and /Authentication/RefreshToken to pass through without modification
        if (
            req.url.indexOf('/dashboard') >= 0 ||
            req.url.indexOf('/Authentication/RefreshToken') >= 0 ||
            req.url.indexOf('/Authentication/AutoLogin') >= 0 ||
            req.url.indexOf('assets') >= 0
        ) {
            return next.handle(req)
        }

        /** Check if the token is still valid */
        if (this._authService.isValidExpiryRefreshToken()) {
            const authResult: AuthenticationLoginResponseModel | null =
                this._authService.getAuthResult()

            if (authResult) {
                /** Clone the request and add the Authorization header */
                const cloned = req.clone({
                    headers: req.headers.set('Authorization', 'Bearer ' + authResult.token),
                })

                return next.handle(cloned).pipe(
                    /** Handle errors to check for token expiration */
                    catchError((error: HttpErrorResponse) => {
                        /** If the status is 401 Unauthorized, redirect to /login */
                        if (error.status === 401) {
                            this._authService.clearAuthResult()
                            this._authService.clearUserResult()
                            this._router.navigate(['/login'])
                        }
                        return throwError(error)
                    })
                )
            } else {
                /** No token available, just pass the request through */
                return next.handle(req)
            }
        } else {
            /** Token is expired, clear stale auth data and pass the request through unauthenticated */
            this._authService.clearAuthResult()
            this._authService.clearUserResult()
            return next.handle(req)
        }
    }
}
